import { useMemo } from "react";
import { Helmet } from "react-helmet-async";

export type SEOOptions = {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: "website" | "article";
  publishedTime?: string;
  modifiedTime?: string;
  tags?: string[];
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  noindex?: boolean;
};

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://www.shishirregmi.com.np";
const DEFAULT_TITLE = "Shishir Regmi | .NET Developer & Software Engineer";
const DEFAULT_DESCRIPTION =
  "Portfolio of Shishir Regmi, a .NET developer specializing in ASP.NET Core, C#, APIs, databases, and scalable backend software.";
const DEFAULT_OG_IMAGE = "/og-image.png";
const TWITTER_HANDLE = "@shishir__regmi";
const DEFAULT_IMAGE_ALT = "Shishir Regmi, .NET developer and software engineer";

const normalizeSiteUrl = SITE_URL.replace(/\/$/, "");

const toAbsoluteUrl = (value?: string | null) => {
  if (!value) return undefined;
  if (value.startsWith("http")) return value;
  const normalized = value.startsWith("/") ? value : `/${value}`;
  return `${normalizeSiteUrl}${normalized}`;
};

export const useSEO = (options: SEOOptions) => {
  const settings = useMemo(() => {
    const requestedUrl =
      options.url ||
      (typeof window !== "undefined" ? window.location.pathname : "/");
    const absoluteCanonical = requestedUrl.startsWith("http")
      ? new URL(requestedUrl)
      : new URL(requestedUrl, `${normalizeSiteUrl}/`);
    absoluteCanonical.search = "";
    absoluteCanonical.hash = "";
    const canonical = absoluteCanonical.toString();

    return {
      title: options.title || DEFAULT_TITLE,
      description: options.description || DEFAULT_DESCRIPTION,
      image: toAbsoluteUrl(options.image || DEFAULT_OG_IMAGE),
      url: canonical,
      type: options.type || "website",
      publishedTime: options.publishedTime,
      modifiedTime: options.modifiedTime,
      tags: options.tags,
      jsonLd: options.jsonLd,
      noindex: options.noindex,
    } as const;
  }, [options.description, options.image, options.jsonLd, options.modifiedTime, options.noindex, options.publishedTime, options.tags, options.title, options.type, options.url]);

  const jsonLdContent = settings.jsonLd ? JSON.stringify(settings.jsonLd) : undefined;

  return (
    <Helmet>
      <title>{settings.title}</title>
      {settings.description && <meta name="description" content={settings.description} />}
      {settings.noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large" />
      )}
      {settings.url && <link rel="canonical" href={settings.url} />}

      <meta property="og:type" content={settings.type} />
      <meta property="og:title" content={settings.title} />
      {settings.description && <meta property="og:description" content={settings.description} />}
      {settings.url && <meta property="og:url" content={settings.url} />}
      <meta property="og:site_name" content="Shishir Regmi" />
      <meta property="og:locale" content="en_US" />
      {settings.image && <meta property="og:image" content={settings.image} />}
      {settings.image && <meta property="og:image:alt" content={DEFAULT_IMAGE_ALT} />}
      {settings.publishedTime && <meta property="article:published_time" content={settings.publishedTime} />}
      {settings.modifiedTime && <meta property="article:modified_time" content={settings.modifiedTime} />}
      {settings.tags?.map((tag) => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:creator" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={settings.title} />
      {settings.description && <meta name="twitter:description" content={settings.description} />}
      {settings.image && <meta name="twitter:image" content={settings.image} />}
      {settings.image && <meta name="twitter:image:alt" content={DEFAULT_IMAGE_ALT} />}

      {jsonLdContent ? (
        <script type="application/ld+json">{jsonLdContent}</script>
      ) : null}
    </Helmet>
  );
};
